import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { PipelineProject, PipelineStage, TherapeuticArea, mapApiProject, mapApiStage, setStages, getStages } from '../../types/pipeline';
import { pipelineAPI } from '../../services/api';
import { chemistryApi } from '../../api/chemistry';
import PipelineView from '../../components/pipeline/PipelineView';
import PipelineTable from '../../components/pipeline/PipelineTable';
import StageBoard from '../../components/pipeline/StageBoard';
import ProjectDetailPanel from '../../components/pipeline/ProjectDetailPanel';

type ViewMode = 'pipeline' | 'table' | 'board';
type StatusFilter = 'all' | 'active' | 'at_risk' | 'cancelled';

const PipelineDashboard: React.FC = () => {
  const [viewMode, setViewMode] = useState<ViewMode>('pipeline');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [taFilter, setTaFilter] = useState<string>('all');
  const [selectedProject, setSelectedProject] = useState<PipelineProject | null>(null);

  const [projects, setProjects] = useState<PipelineProject[]>([]);
  const [stages, setStagesState] = useState<PipelineStage[]>([]);
  const [therapeuticAreas, setTherapeuticAreas] = useState<TherapeuticArea[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Create Program modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newProgramName, setNewProgramName] = useState('');
  const [newProgramDescription, setNewProgramDescription] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const handleCreateProgram = async () => {
    if (!newProgramName.trim()) return;
    setIsCreating(true);
    setCreateError(null);
    try {
      await chemistryApi.createProject({
        name: newProgramName.trim(),
        description: newProgramDescription.trim() || undefined,
      });
      setNewProgramName('');
      setNewProgramDescription('');
      setShowCreateModal(false);
      fetchData();
    } catch (err: any) {
      setCreateError(err?.response?.data?.detail || err.message || 'Failed to create program');
    } finally {
      setIsCreating(false);
    }
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [stagesRes, taRes, portfolioRes] = await Promise.all([
        pipelineAPI.getStages(),
        pipelineAPI.getTherapeuticAreas(),
        pipelineAPI.getPortfolioSummary(),
      ]);

      const mappedStages = stagesRes.data.map(mapApiStage);
      setStages(mappedStages);
      setStagesState(mappedStages);
      setTherapeuticAreas(taRes.data);
      setProjects(portfolioRes.data.map(mapApiProject));
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to load pipeline data';
      setError(msg);
      console.error('Pipeline fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const filtered = useMemo(() => {
    let result = projects;
    if (statusFilter !== 'all') {
      result = result.filter((p) => p.status === statusFilter);
    }
    if (taFilter !== 'all') {
      result = result.filter((p) => p.indicationCode === taFilter);
    }
    return result;
  }, [projects, statusFilter, taFilter]);

  // Summary stats
  const activeCount = projects.filter((p) => p.status === 'active').length;
  const atRiskCount = projects.filter((p) => p.status === 'at_risk').length;
  const cancelledCount = projects.filter((p) => p.status === 'cancelled').length;
  const totalCompounds = projects.reduce((s, p) => s + p.compounds, 0);

  const STATUS_PILLS: { key: StatusFilter; label: string }[] = [
    { key: 'all', label: 'All' },
    { key: 'active', label: 'Active' },
    { key: 'at_risk', label: 'At Risk' },
    { key: 'cancelled', label: 'Cancelled' },
  ];

  const VIEW_MODES: { key: ViewMode; label: string; icon: React.ReactNode }[] = [
    {
      key: 'pipeline',
      label: 'Pipeline',
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
        </svg>
      ),
    },
    {
      key: 'table',
      label: 'Table',
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M3 14h18M3 6h18M3 18h18" />
        </svg>
      ),
    },
    {
      key: 'board',
      label: 'Board',
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2" />
        </svg>
      ),
    },
  ];

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-teal-500 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          <p className="text-sm text-gray-500 dark:text-gray-400">Loading pipeline data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-full flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center max-w-md">
          <div className="w-12 h-12 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-3">
            <svg className="w-6 h-6 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <p className="text-sm text-gray-700 dark:text-gray-200 font-medium mb-1">Failed to load pipeline</p>
          <p className="text-xs text-gray-400 dark:text-gray-500 mb-3">{error}</p>
          <button onClick={fetchData} className="px-4 py-2 text-sm font-medium text-white bg-teal-600 rounded-lg hover:bg-teal-700">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-gradient-to-r from-teal-600 to-teal-700 px-6 py-5">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white">Discovery Pipeline</h1>
            <p className="text-teal-100 text-sm mt-0.5">
              {activeCount} active programs &middot; {totalCompounds.toLocaleString()} compounds
            </p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="px-4 py-2 text-sm font-medium text-teal-700 bg-white rounded-lg hover:bg-teal-50 dark:bg-teal-900 dark:text-teal-200 dark:hover:bg-teal-800 transition-colors shadow-sm"
          >
            + New Program
          </button>
        </div>
      </div>

      {/* Summary cards */}
      <div className="px-6 -mt-3">
        <div className="grid grid-cols-3 gap-3">
          <SummaryCard label="Active" value={activeCount} color="emerald" onClick={() => setStatusFilter(statusFilter === 'active' ? 'all' : 'active')} />
          <SummaryCard label="At Risk" value={atRiskCount} color="red" onClick={() => setStatusFilter(statusFilter === 'at_risk' ? 'all' : 'at_risk')} />
          <SummaryCard label="Cancelled" value={cancelledCount} color="gray" onClick={() => setStatusFilter(statusFilter === 'cancelled' ? 'all' : 'cancelled')} />
        </div>
      </div>

      {/* Toolbar */}
      <div className="px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          {/* View mode switcher */}
          <div className="flex items-center bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-0.5 shadow-sm">
            {VIEW_MODES.map((vm) => (
              <button
                key={vm.key}
                onClick={() => setViewMode(vm.key)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  viewMode === vm.key
                    ? 'bg-teal-500 text-white shadow-sm'
                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                }`}
              >
                {vm.icon}
                {vm.label}
              </button>
            ))}
          </div>

          {/* Therapeutic area filter */}
          <select
            value={taFilter}
            onChange={(e) => setTaFilter(e.target.value)}
            className="px-3 py-1.5 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 shadow-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
          >
            <option value="all">All Therapeutic Areas</option>
            {therapeuticAreas.map((ta) => (
              <option key={ta.code} value={ta.code}>{ta.name}</option>
            ))}
          </select>
        </div>

        {/* Status filter pills */}
        <div className="flex items-center gap-1.5">
          {STATUS_PILLS.map((pill) => (
            <button
              key={pill.key}
              onClick={() => setStatusFilter(pill.key)}
              className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                statusFilter === pill.key
                  ? 'bg-gray-800 text-white dark:bg-gray-200 dark:text-gray-900'
                  : 'bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400 border border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
              }`}
            >
              {pill.label}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto px-6 pb-6">
        {filtered.length === 0 ? (
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-12 text-center">
            <p className="text-gray-400 dark:text-gray-500 text-sm">No programs match the current filters</p>
          </div>
        ) : (
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden">
            {viewMode === 'pipeline' && (
              <div className="p-4">
                <PipelineView projects={filtered} stages={stages} onSelectProject={setSelectedProject} />
              </div>
            )}
            {viewMode === 'table' && (
              <PipelineTable projects={filtered} onSelectProject={setSelectedProject} />
            )}
            {viewMode === 'board' && (
              <div className="p-4">
                <StageBoard projects={filtered} stages={stages} onSelectProject={setSelectedProject} onProjectUpdated={fetchData} />
              </div>
            )}
          </div>
        )}
      </div>

      {/* Detail panel */}
      <ProjectDetailPanel
        project={selectedProject}
        onClose={() => setSelectedProject(null)}
        onProjectUpdated={fetchData}
      />

      {/* Backdrop */}
      {selectedProject && (
        <div
          className="fixed inset-0 bg-black/20 z-40 transition-opacity duration-300"
          onClick={() => setSelectedProject(null)}
        />
      )}

      {/* Create Program Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-6 w-[440px] max-w-[90vw]">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">New Program</h3>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Program Name *</label>
                <input
                  value={newProgramName}
                  onChange={e => setNewProgramName(e.target.value)}
                  placeholder="Enter program name..."
                  className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-teal-500"
                  autoFocus
                  onKeyDown={e => e.key === 'Enter' && handleCreateProgram()}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Description <span className="text-gray-400 font-normal">(optional)</span></label>
                <textarea
                  value={newProgramDescription}
                  onChange={e => setNewProgramDescription(e.target.value)}
                  rows={2}
                  placeholder="Optional description..."
                  className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-teal-500 resize-none"
                />
              </div>
              {createError && (
                <div className="text-xs text-red-600 dark:text-red-400 p-2 rounded-lg bg-red-50 dark:bg-red-900/20">{createError}</div>
              )}
              <div className="flex justify-end gap-2 pt-2">
                <button
                  onClick={() => { setShowCreateModal(false); setCreateError(null); }}
                  className="px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-400 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateProgram}
                  disabled={!newProgramName.trim() || isCreating}
                  className="px-4 py-2 text-sm font-semibold text-white bg-teal-600 rounded-lg hover:bg-teal-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  {isCreating ? 'Creating...' : 'Create Program'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/* ---------- Summary Card ---------- */
interface SummaryCardProps {
  label: string;
  value: number | string;
  color: string;
  sub?: string;
  onClick?: () => void;
}

const COLOR_MAP: Record<string, { bg: string; text: string; valueTxt: string }> = {
  emerald: { bg: 'bg-emerald-50 dark:bg-emerald-900/30', text: 'text-emerald-600 dark:text-emerald-400', valueTxt: 'text-emerald-700 dark:text-emerald-300' },
  red: { bg: 'bg-red-50 dark:bg-red-900/30', text: 'text-red-600 dark:text-red-400', valueTxt: 'text-red-700 dark:text-red-300' },
  amber: { bg: 'bg-amber-50 dark:bg-amber-900/30', text: 'text-amber-600 dark:text-amber-400', valueTxt: 'text-amber-700 dark:text-amber-300' },
  gray: { bg: 'bg-gray-50 dark:bg-gray-800', text: 'text-gray-500 dark:text-gray-400', valueTxt: 'text-gray-600 dark:text-gray-300' },
  blue: { bg: 'bg-blue-50 dark:bg-blue-900/30', text: 'text-blue-600 dark:text-blue-400', valueTxt: 'text-blue-700 dark:text-blue-300' },
};

const SummaryCard: React.FC<SummaryCardProps> = ({ label, value, color, sub, onClick }) => {
  const c = COLOR_MAP[color] || COLOR_MAP.gray;
  return (
    <div
      onClick={onClick}
      className={`${c.bg} rounded-xl p-4 border border-white dark:border-gray-700 shadow-sm ${onClick ? 'cursor-pointer hover:ring-2 hover:ring-teal-400/50 transition-shadow duration-150' : ''}`}
    >
      <p className={`text-xs font-medium ${c.text} uppercase tracking-wide`}>{label}</p>
      <p className={`text-2xl font-bold ${c.valueTxt} mt-1`}>{value}</p>
      {sub && <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">{sub}</p>}
    </div>
  );
};

export default PipelineDashboard;
